import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCmsList } from '../hooks/useCms';
import { FALLBACK_PARTNERS, FALLBACK_SPONSOR } from '../data/cmsFallbacks';
import { ExternalLink, Handshake } from 'lucide-react';
import SEO from '../components/SEO';
import { seoData, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';

// Golden Glow Line Component
const GoldenGlowLine = () => (
  <div className="relative h-[2px] bg-black overflow-hidden">
    <div 
      className="absolute top-0 h-full w-[300px]"
      style={{
        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
        animation: 'glowLine 4s ease-in-out infinite',
        boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
      }}
    />
    <style>{`
      @keyframes glowLine {
        0% { left: -300px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { left: 100%; opacity: 0; }
      }
    `}</style>
  </div>
);

// Animated Corner Frame Component
const AnimatedCornerFrame = ({ children, className = "" }) => (
  <div className={`group relative ${className}`}>
    {/* Top-left corner frame */}
    <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-3xl pointer-events-none">
      <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
        <div 
          className="absolute left-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVertical 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
        <div 
          className="absolute top-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    
    {/* Bottom-right corner frame */}
    <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-3xl pointer-events-none">
      <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
        <div 
          className="absolute right-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
        <div 
          className="absolute bottom-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    {children}
    <style>{`
      @keyframes cornerGlowVertical {
        0% { top: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { top: 100%; opacity: 0; }
      }
      @keyframes cornerGlowHorizontal {
        0% { left: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { left: 100%; opacity: 0; }
      }
      @keyframes cornerGlowVerticalReverse {
        0% { bottom: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { bottom: 100%; opacity: 0; }
      }
      @keyframes cornerGlowHorizontalReverse {
        0% { right: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { right: 100%; opacity: 0; }
      }
    `}</style>
  </div>
);

const PartnersPage = () => {
  const { t } = useLanguage();
  const allPartners = useCmsList('/api/cms/partners', FALLBACK_PARTNERS);
  const flagged = allPartners.filter((p) => p.is_sponsor);
  const sponsors = flagged.length ? flagged : [FALLBACK_SPONSOR];
  const partners = allPartners.filter((p) => !p.is_sponsor);
  
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.partners.title}
        description={seoData.partners.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">{t('collab', { de: 'Zusammenarbeit', en: 'Collaboration' })}</span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">{t('title', translations.partners)}</h1>
              <p className="text-xl text-black/60 max-w-2xl mx-auto">{t('subtitle', translations.partners)}</p>
            </div>
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-12">
                <Handshake className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="text-2xl font-bold text-black">{t('sponsor', translations.partners)}</h2>
              </div>
              <div className={`grid gap-8 ${sponsors.length > 1 ? 'md:grid-cols-2' : ''}`}>
              {sponsors.map((sponsor) => (
              <AnimatedCornerFrame key={sponsor.id} className="p-12 rounded-3xl bg-white border border-black/5 hover:border-[#D4AF37]/30 transition-all duration-500 shadow-xl">
                <div className="flex flex-col lg:flex-row items-center gap-12">
                  <div className="flex-shrink-0">
                    <div className="relative w-56 h-56 rounded-2xl bg-[#F9F9F9] p-8 flex items-center justify-center">
                      <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <h3 className="text-3xl font-bold text-black mb-4">{sponsor.name}</h3>
                    <p className="text-black/60 text-lg leading-relaxed mb-6">{t('d', { de: sponsor.description_de, en: sponsor.description_en }) || t('sponsorDesc', translations.partners)}</p>
                    <a href={sponsor.website_url || FALLBACK_SPONSOR.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#003366] hover:bg-[#C9A227] rounded-lg transition-all duration-300 font-medium">
                      <span>{t('visitWebsite', { de: 'Website besuchen', en: 'Visit Website' })}</span><ExternalLink size={18} />
                    </a>
                  </div>
                </div>
              </AnimatedCornerFrame>
              ))}
              </div>
            </div>
          </div>
        </section>
        <GoldenGlowLine />
        <section className="py-20 bg-[#F9F9F9]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-12">
              <Handshake className="w-6 h-6 text-[#D4AF37]" />
              <h2 className="text-2xl font-bold text-black">{t('hotelPartners', translations.partners)}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {partners.map((partner) => (
                <div key={partner.id} className="group p-8 rounded-2xl bg-white border border-black/5 hover:border-[#D4AF37]/30 hover:shadow-lg transition-all duration-500 flex flex-col items-center justify-center">
                  <div className="w-full h-20 flex items-center justify-center mb-4">
                    <img src={partner.logo_url} alt={partner.name} className="max-w-full max-h-full object-contain opacity-100 group-hover:scale-110 transition-all duration-500" />
                  </div>
                  <p className="text-black/60 text-sm text-center group-hover:text-[#D4AF37] transition-colors">{partner.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PartnersPage;
